import { useI18n } from "../i18n/I18nProvider";
import { useGeo } from "../lib/geo";
import { Combobox } from "./Combobox";
import { Field } from "./form";

export interface AddressValue {
  province: string;
  city: string;
  addressLine: string;
  addressLine2: string;
  postalCode: string;
}

interface Props {
  idPrefix: string;
  value: AddressValue;
  onChange: (patch: Partial<AddressValue>) => void;
  /** "place": province + city; "street": street lines + postal code; "all": both (profile). */
  show: "place" | "street" | "all";
  errors?: Partial<Record<"province" | "city" | "addressLine", string | null>>;
  /** A city was picked from the list: its province (Georef id) and partido/comuna, e.g. to suggest a zone. */
  onPlace?: (provinceId: string, department: string) => void;
}

/** Province and city as type-ahead selects (official list; the city depends on the province), plus the street. */
export function AddressFields({ idPrefix, value, onChange, show, errors = {}, onPlace }: Props) {
  const { t } = useI18n();
  const { provinces, province, localities } = useGeo(value.province);
  const caba = province?.id === "02";

  return (
    <div className="address-fields">
      {show !== "street" && (
      <div className="profile-grid">
        <Combobox
          id={`${idPrefix}-province`}
          label={t("address.province")}
          options={provinces.map((p) => ({ label: p.name }))}
          value={value.province}
          onChange={(v) => v !== value.province && onChange({ province: v, city: "" })}
          placeholder={t("address.chooseProvince")}
          emptyText={t("address.noProvince")}
          autoComplete="address-level1"
          required
          error={errors.province}
          maxSuggestions={30}
        />
        <Combobox
          id={`${idPrefix}-city`}
          label={t(caba ? "address.barrio" : "address.city")}
          options={localities.map(([name, department]) => ({ label: name, hint: department && department !== name ? department : undefined }))}
          value={value.city}
          onChange={(v, o) => {
            onChange({ city: v });
            if (province) onPlace?.(province.id, o?.hint ?? (o ? o.label : ""));
          }}
          placeholder={province ? t("address.typeCity") : t("address.provinceFirst")}
          disabled={!province}
          allowCustom
          emptyText={t("address.noCity")}
          autoComplete="address-level2"
          required
          error={errors.city}
        />
      </div>
      )}
      {show !== "place" && (
        <>
          <Field
            id={`${idPrefix}-line1`}
            label={t("address.line1")}
            autoComplete="address-line1"
            maxLength={200}
            value={value.addressLine}
            onChange={(e) => onChange({ addressLine: e.target.value })}
            error={errors.addressLine}
          />
          <Field
            id={`${idPrefix}-line2`}
            label={t("address.line2")}
            autoComplete="address-line2"
            maxLength={200}
            value={value.addressLine2}
            onChange={(e) => onChange({ addressLine2: e.target.value })}
          />
          <div className="address-postal">
            <Field id={`${idPrefix}-postal`} label={t("address.postalCode")} autoComplete="postal-code" inputMode="numeric" maxLength={12} value={value.postalCode} onChange={(e) => onChange({ postalCode: e.target.value })} />
          </div>
        </>
      )}
    </div>
  );
}
